/**
 * Same as yourdoctors-patientportal src/utils/pharmacyLocation.js — keep the two
 * in step: PHD checks a prescription against the fields saved here.
 *
 * The pharmacy's country and province/state, kept as fields on
 * users/{uid}/pharmacy so a prescription can be checked against them.
 *
 * Never read from the free-text address: a city name alone is ambiguous
 * (Vancouver is in British Columbia and in Washington). The fields come from
 * the pharmacy search result's own country/province, or from the PCA choosing
 * them in the add/edit pharmacy form.
 *
 * `locationForAddress` is the address the fields were saved with. The patient
 * apps can change a pharmacy's address without knowing about these fields, so a
 * location is trusted only while the address it was saved with is still the
 * pharmacy's address.
 */

export const COUNTRIES = ['Canada', 'United States']

export const CANADA_PROVINCES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon',
]

export const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois',
  'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland',
  'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana',
  'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah',
  'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
]

export const regionsFor = (country) =>
  country === 'Canada' ? CANADA_PROVINCES : country === 'United States' ? US_STATES : []

const CANADA_CODES = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador', NT: 'Northwest Territories', NS: 'Nova Scotia',
  NU: 'Nunavut', ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec',
  SK: 'Saskatchewan', YT: 'Yukon',
}

/**
 * A search result's own province/state value as one of the dropdown names, or
 * '' when it is not one. Accepts the full name (any case) or, for Canada, the
 * two-letter code the provincial pharmacy directory may return.
 */
export const normalizeRegion = (country, value) => {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const list = regionsFor(country)
  const byName = list.find((name) => name.toLowerCase() === raw.toLowerCase())
  if (byName) return byName
  if (raw.toLowerCase() === 'québec') return 'Quebec'
  if (country === 'Canada') return CANADA_CODES[raw.toUpperCase()] || ''
  return ''
}

/** The country a search result is from, as a dropdown name, or ''. */
export const normalizeCountry = (value) => {
  const raw = String(value || '').trim().toLowerCase()
  if (raw === 'canada' || raw === 'ca') return 'Canada'
  if (['united states', 'united states of america', 'usa', 'us'].includes(raw)) {
    return 'United States'
  }
  return ''
}

/** The fields to save with a pharmacy, or null when the location is incomplete. */
export const pharmacyLocationFields = ({ country, province, address }) => {
  const c = normalizeCountry(country)
  const p = normalizeRegion(c, province)
  if (!c || !p) return null
  return { country: c, province: p, locationForAddress: String(address || '') }
}

/**
 * The pharmacy's confirmed location, or null when it has none — never saved, or
 * saved for a different address than the pharmacy has now.
 */
export const confirmedPharmacyLocation = (pharmacy) => {
  if (!pharmacy || typeof pharmacy !== 'object') return null
  const country = normalizeCountry(pharmacy.country)
  const province = normalizeRegion(country, pharmacy.province)
  if (!country || !province) return null
  if (String(pharmacy.locationForAddress ?? '') !== String(pharmacy.address ?? '')) return null
  return { country, province }
}
